import { GetStatusesQuery, StatusFilterInput } from '../gql/graphql';
import { useCallback, useMemo, useEffect, useState } from 'react';

import { Flatten } from './types';
import { SortOrder } from '@converge/data';
import { getPaginatedNodes } from './utils';
import { graphql } from '../gql';
import { useQuery } from '@apollo/client';

export const GET_STATUSES = graphql(`
  query getStatuses(
    $filters: StatusFilterInput
    $pagination: PaginationRequest
    $sort: PaginationSort
  ) {
    statuses(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          uuid
          targetType
          targetName
          targetUuid
          statusKey
          statusValue
          statusArea
          statusName
          statusType
          statusDagId
          createdAt
          updatedAt
          user {
            node {
              uuid
              username
            }
          }
        }
      }
    }
  }
`);

export const useShouldPoll = () => {
  const [poll, setPoll] = useState<boolean>(true);

  const useUpdatePoll = useCallback(
    (shouldPoll: boolean) => {
      if (shouldPoll !== poll) {
        setPoll(shouldPoll);
      }
    },
    [poll]
  );

  return {
    poll,
    setPoll: useUpdatePoll,
  };
};

export function useStatusesQuery({
  skip = false,
  poll = false,
  appendedStatuses = [],
  prependedStatuses = [],
  sort = SortOrder.Desc,
  operationName = 'default',
  filters,
}: {
  filters: StatusFilterInput;
  skip?: boolean;
  poll?: boolean;
  prependedStatuses?: GetStatusesQueryStatus[];
  appendedStatuses?: GetStatusesQueryStatus[];
  sort?: SortOrder;
  operationName: string;
}) {
  const [shouldPoll, setShouldPoll] = useState<boolean>(false);

  const { loading, error, data, refetch, networkStatus } = useQuery(
    GET_STATUSES,
    {
      variables: {
        filters,
        sort: {
          key: 'updated_at',
          order: sort ?? 'DESC',
        },
        pagination: {
          first: 100,
        },
      },
      skip,
      context: {
        operationName: `getStatuses_${operationName}`,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }
  );

  const info = useMemo(
    () =>
      getInfo(data, {
        appendedStatuses,
        prependedStatuses,
      }),
    [appendedStatuses, data, prependedStatuses]
  );

  useEffect(() => {
    if (info.shouldPoll !== shouldPoll) {
      setShouldPoll(info.shouldPoll);
    }
  }, [info.shouldPoll, shouldPoll]);

  return {
    loading,
    error,
    data,
    info,
    shouldPoll,
    refetch,
    networkStatus,
  };
}

const getInfo = (
  data?: GetStatusesQuery,
  {
    prependedStatuses = [],
    appendedStatuses = [],
  }: {
    prependedStatuses?: GetStatusesQueryStatus[];
    appendedStatuses?: GetStatusesQueryStatus[];
  } = {}
) => {
  const statuses = [...getPaginatedNodes(data?.statuses)];

  if (prependedStatuses?.length) {
    statuses.unshift(...prependedStatuses);
  }

  if (appendedStatuses?.length) {
    statuses.push(...appendedStatuses);
  }

  const latestStatus = statuses[0];

  const totalResults =
    typeof data?.statuses.pageInfo.totalResults === 'number'
      ? data?.statuses.pageInfo.totalResults +
        (appendedStatuses?.length ?? 0) +
        (prependedStatuses?.length ?? 0)
      : data?.statuses.pageInfo.totalResults;

  const shouldPoll = statuses.some(
    (status) => !new StatusInfo(status.statusValue).finished
  );

  return {
    statuses,
    latestStatus,
    totalResults,
    shouldPoll,
  };
};

export type SubmissionStatusInfo = ReturnType<typeof getInfo>;

export type GetStatusesQueryStatus = Flatten<
  GetStatusesQuery['statuses']['edges']
>['node'];

export enum StatusValue {
  Cancelled = 'cancelled',
  Completed = 'completed',
  Failed = 'failed',
  InProgress = 'in-progress',
  InProcess = 'in-process',
  Pending = 'pending',
  Skipped = 'skipped',
  Other = 'other',
}

export const statusValueToStatus = (
  statusValue: string | undefined | null
): StatusValue => {
  switch (statusValue) {
    case StatusValue.Skipped:
      return StatusValue.Skipped;
    case StatusValue.Cancelled:
      return StatusValue.Cancelled;
    case StatusValue.Completed:
      return StatusValue.Completed;
    case StatusValue.Failed:
      return StatusValue.Failed;
    case StatusValue.InProgress:
      return StatusValue.InProgress;
    case StatusValue.InProcess:
      return statusValueToStatus(StatusValue.InProgress);
    case StatusValue.Pending:
      return StatusValue.Pending;
    default:
      return StatusValue.Other;
  }
};

export class StatusInfo {
  public text: StatusValue | string | undefined | null;
  public statusValue: StatusValue;

  constructor(statusValue: StatusValue | string | undefined | null) {
    this.text = statusValue;
    this.statusValue = statusValueToStatus(statusValue);
  }

  get skipped() {
    return this.statusValue === StatusValue.Skipped;
  }

  get cancelled() {
    return this.statusValue === StatusValue.Cancelled;
  }

  get loading() {
    return this.statusValue === StatusValue.InProgress;
  }

  get pending() {
    return this.statusValue === StatusValue.Pending;
  }

  get completed() {
    return this.statusValue === StatusValue.Completed;
  }

  get success() {
    return this.completed;
  }

  get failed() {
    return this.statusValue === StatusValue.Failed;
  }

  get finished() {
    return this.completed || this.failed || this.skipped || this.cancelled;
  }
}

export const statusValueToStatusInfo = (
  statusValue: string | undefined | null
) => {
  return new StatusInfo(statusValue);
};
